import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AppState } from '../../app/store';
import { getStages, getCheckIns, CheckInRecord } from '../../utils/airtable-api';
import { Stage } from './stage';
import { FetchStatus } from '../../utils/fetch-status';
import { StageRecord } from '../../utils/airtable-api';
import { CheckIn, checkinTypeFromString } from './checkin';

type Route = {
  status: FetchStatus;
  data: null | Stage[];
  error: null | string;
}

type CheckInSchedule = {
  status: FetchStatus;
  data: null | CheckIn[];
  error: null | string;
}

export type PilgrimageState = {
  id: string | null;
  year: number | null;
  church: string | null;
  homeChurch: string | null;
  name: string | null;
  route: Route;
  cached_route: Route; 
  checkins: CheckInSchedule;
};

export const fetchStages = createAsyncThunk(
  'pilgrimage/fetchStages',
  async(pilgrimageId : string) => getStages(pilgrimageId)
);

export const fetchCheckIns = createAsyncThunk(
  'pilgrimage/fetchCheckIns',
  async(pilgrimageId : string) => getCheckIns(pilgrimageId)
);


export const pilgrimageSlice = createSlice({
  name: 'pilgrimage',
  initialState: {
    id: null,
    year: null,
    church: null,
    name: null,
    route: {
      status: FetchStatus.Idle,
      data: null,
      error: null
    },
    cached_route: {
      status: FetchStatus.Idle,
      data: null,
      error: null
    },
    checkins: {
      status: FetchStatus.Idle,
      data: null,
      error: null
    }
  } as PilgrimageState,
  reducers: {
    setPilgrimage: (state, action: { payload: PilgrimageState }) => state = action.payload,
  },
  extraReducers: builder => {
    builder.addCase(fetchStages.pending, (state) => {
      state.route.status = FetchStatus.Pending;
      state.route.data = null;
    });
    builder.addCase(fetchStages.fulfilled, (state, action) => {
      state.route.status = FetchStatus.Fulfilled;
      state.route.error = null;
      state.route.data = action.payload.records.map((record : StageRecord) => ({
        start: {
          name: record.fields.StartName[0],
          lat: record.fields.StartLat[0],
          lng: record.fields.StartLng[0]
        },
        end: {
          name: record.fields.EndName[0],
          lat: record.fields.EndLat[0],
          lng: record.fields.EndLng[0]
        },
        distance: record.fields.distance,
        image: record.fields.image ? record.fields.image[0].url : undefined,
        directions: record.fields.Directions ? record.fields.Directions[0].url : undefined,
      }));
      state.cached_route = state.route;
    });
    builder.addCase(fetchStages.rejected, (state, action) => {
      if(state.cached_route.status === FetchStatus.Fulfilled) {
        state.route = state.cached_route;
      } else {
        state.route.status = FetchStatus.Rejected;
        state.route.error = action.error.message || null;
      }
    });
    builder.addCase(fetchCheckIns.pending, (state) => {
      state.checkins.status = FetchStatus.Pending;
      state.checkins.data = null;
    });
    builder.addCase(fetchCheckIns.fulfilled, (state, action) => {
      state.checkins.status = FetchStatus.Fulfilled;
      state.checkins.error = null;
      state.checkins.data = action.payload.records.map((record : CheckInRecord) => ({
        name: record.fields.Name,
        datetime: record.fields.Date,
        type: checkinTypeFromString(record.fields.Type),
        location: record.fields.Location,
        calendar: record.fields.Calendar
      }));
    });
    builder.addCase(fetchCheckIns.rejected, (state, action) => {
      state.checkins.status = FetchStatus.Rejected;
      state.checkins.error = action.error.message || null;
      state.checkins.data = null;
    });
  }
});

export const { setPilgrimage } = pilgrimageSlice.actions;

export const selectPilgrimage = (state : AppState) => state.pilgrimage;
export const selectStages = (state: AppState) => state.pilgrimage.route.status === FetchStatus.Fulfilled ? state.pilgrimage.route.data : [];
export const selectRoute = (state: AppState) => state.pilgrimage.route;
export const selectCheckIns = (state: AppState) => state.pilgrimage.checkins;

export default pilgrimageSlice.reducer;